import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import {
  ArrowRight,
  CalendarPlus,
  CalendarSlash,
  CircleNotch,
  FloppyDisk,
  Prohibit,
  Trash,
} from "@phosphor-icons/react";
import { TimeInput } from "./TimeInput";
import { formatDateWithOptionsBs } from "@/lib/format";

import type { DateOverride } from "@/types";
import type { DateRange } from "react-day-picker";

interface DateOverridesProps {
  overrides: DateOverride[];
  onAdd: (data: { date: string; type: "add" | "block"; startTime?: string; endTime?: string }) => void;
  onAddRange: (data: { startDate: string; endDate: string }) => void;
  onDelete: (id: string) => void;
  isAdding: boolean;
  isAddingRange: boolean;
  isDeleting: boolean;
}

function fmt(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

const MONTH_LONG_HR = [
  "Januar", "Februar", "Mart", "April", "Maj", "Juni",
  "Juli", "August", "Septembar", "Oktobar", "Novembar", "Decembar",
];

type MonthGroup = { key: string; label: string; items: DateOverride[] };

function groupByMonth(overrides: DateOverride[]): MonthGroup[] {
  const sorted = [...overrides].sort((a, b) => a.date.localeCompare(b.date));
  const map = new Map<string, MonthGroup>();
  for (const o of sorted) {
    const d = new Date(o.date + "T00:00:00");
    const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, "0")}`;
    if (!map.has(key)) {
      map.set(key, { key, label: `${MONTH_LONG_HR[d.getMonth()]} ${d.getFullYear()}`, items: [] });
    }
    map.get(key)!.items.push(o);
  }
  return Array.from(map.values());
}

const CAL_MODIFIER_CLASSNAMES = {
  blocked: "!bg-rose-50 !text-rose-600 !line-through",
  added: "!bg-emerald-50 !text-emerald-700 !font-semibold",
};

export function DateOverrides({
  overrides,
  onAdd,
  onAddRange,
  onDelete,
  isAdding,
  isAddingRange,
  isDeleting,
}: DateOverridesProps) {
  const [mode, setMode] = useState<"single" | "range">("single");
  const [overrideDate, setOverrideDate] = useState<Date | undefined>();
  const [overrideType, setOverrideType] = useState<"block" | "add">("block");
  const [overrideStart, setOverrideStart] = useState("09:00");
  const [overrideEnd, setOverrideEnd] = useState("17:00");
  const [range, setRange] = useState<DateRange | undefined>();

  const prevAdding = useRef(isAdding);
  useEffect(() => {
    if (prevAdding.current && !isAdding) {
      setOverrideDate(undefined);
      setOverrideStart("09:00");
      setOverrideEnd("17:00");
    }
    prevAdding.current = isAdding;
  }, [isAdding]);

  const prevAddingRange = useRef(isAddingRange);
  useEffect(() => {
    if (prevAddingRange.current && !isAddingRange) {
      setRange(undefined);
    }
    prevAddingRange.current = isAddingRange;
  }, [isAddingRange]);

  const blockedDates = overrides.filter((o) => o.type === "block").map((o) => new Date(o.date + "T00:00:00"));
  const addedDates = overrides.filter((o) => o.type === "add").map((o) => new Date(o.date + "T00:00:00"));

  // Same rule as availabilityOverrideSchema; "HH:MM" strings compare in time order.
  const addRangeInvalid = overrideType === "add" && overrideStart >= overrideEnd;

  const handleAddOverride = () => {
    if (!overrideDate || addRangeInvalid) return;
    const payload: { date: string; type: "add" | "block"; startTime?: string; endTime?: string } = {
      date: fmt(overrideDate),
      type: overrideType,
    };
    if (overrideType === "add") {
      payload.startTime = overrideStart;
      payload.endTime = overrideEnd;
    }
    onAdd(payload);
  };

  const handleAddRange = () => {
    if (!range?.from || !range?.to) return;
    onAddRange({ startDate: fmt(range.from), endDate: fmt(range.to) });
  };

  const rangeLabel =
    range?.from && range?.to
      ? `${formatDateWithOptionsBs(range.from, { day: "numeric", month: "short" })} - ${formatDateWithOptionsBs(range.to, { day: "numeric", month: "short", year: "numeric" })}`
      : range?.from
        ? formatDateWithOptionsBs(range.from, { day: "numeric", month: "short", year: "numeric" })
        : null;

  const groups = groupByMonth(overrides);

  return (
    <div className="grid gap-5 lg:grid-cols-[1.1fr_1fr]">
      {/* LEFT: calendar + action */}
      <section className="rounded-xl border border-border/60 p-4">
        <div className="mb-4 inline-flex rounded-lg border border-border/60 p-0.5">
          {([
            { k: "single", label: "Pojedinačno" },
            { k: "range", label: "Raspon" },
          ] as const).map((opt) => {
            const active = mode === opt.k;
            return (
              <button
                key={opt.k}
                type="button"
                aria-pressed={active}
                onClick={() => setMode(opt.k)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                  active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        <div className="flex justify-center">
          {mode === "single" ? (
            <Calendar
              key="single"
              mode="single"
              selected={overrideDate}
              onSelect={setOverrideDate}
              disabled={{ before: new Date() }}
              modifiers={{ blocked: blockedDates, added: addedDates }}
              modifiersClassNames={CAL_MODIFIER_CLASSNAMES}
              className="[--cell-size:--spacing(9)]"
            />
          ) : (
            <Calendar
              key="range"
              mode="range"
              selected={range}
              onSelect={setRange}
              disabled={{ before: new Date() }}
              numberOfMonths={1}
              modifiers={{ blocked: blockedDates, added: addedDates }}
              modifiersClassNames={CAL_MODIFIER_CLASSNAMES}
              className="[--cell-size:--spacing(9)]"
            />
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block size-2.5 rounded-sm bg-rose-100 ring-1 ring-rose-200" />
            Blokirano
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block size-2.5 rounded-sm bg-emerald-100 ring-1 ring-emerald-200" />
            Dodato
          </span>
        </div>

        {/* Single-date action */}
        {mode === "single" && overrideDate && (
          <div className="mt-4 border-t border-border/60 pt-4">
            <p className="text-sm font-medium text-foreground first-letter:uppercase">
              {formatDateWithOptionsBs(overrideDate, { weekday: "long", day: "numeric", month: "long" })}
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setOverrideType("block")}
                aria-pressed={overrideType === "block"}
                className={cn(
                  "inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors",
                  overrideType === "block"
                    ? "border-rose-300 bg-rose-50 text-rose-700"
                    : "border-border text-muted-foreground hover:border-rose-200 hover:text-rose-600",
                )}
              >
                <Prohibit className="size-4" />
                Blokiraj dan
              </button>
              <button
                type="button"
                onClick={() => setOverrideType("add")}
                aria-pressed={overrideType === "add"}
                className={cn(
                  "inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors",
                  overrideType === "add"
                    ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                    : "border-border text-muted-foreground hover:border-emerald-200 hover:text-emerald-600",
                )}
              >
                <CalendarPlus className="size-4" />
                Dodaj termin
              </button>
            </div>

            {overrideType === "add" && (
              <div className="mt-3 flex items-center justify-center gap-3">
                <TimeInput value={overrideStart} onChange={setOverrideStart} />
                <ArrowRight className="size-3.5 text-muted-foreground/50" />
                <TimeInput value={overrideEnd} onChange={setOverrideEnd} />
              </div>
            )}

            {addRangeInvalid && (
              <p className="mt-2 text-center text-xs text-destructive">Početak mora biti prije kraja.</p>
            )}

            <Button onClick={handleAddOverride} disabled={isAdding || addRangeInvalid} className="mt-4 w-full">
              {isAdding ? <CircleNotch className="size-4 animate-spin" /> : <FloppyDisk className="size-4" />}
              Spremi izuzetak
            </Button>
          </div>
        )}

        {/* Range action */}
        {mode === "range" && rangeLabel && (
          <div className="mt-4 border-t border-border/60 pt-4">
            <p className="text-sm font-medium text-foreground">{rangeLabel}</p>
            <Button
              onClick={handleAddRange}
              disabled={isAddingRange || !range?.to}
              variant="destructive"
              className="mt-3 w-full"
            >
              {isAddingRange ? <CircleNotch className="size-4 animate-spin" /> : <CalendarSlash className="size-4" />}
              Blokiraj cijeli raspon
            </Button>
            {!range?.to && (
              <p className="mt-2 text-center text-xs text-muted-foreground">
                Odaberite i krajnji datum da nastavite.
              </p>
            )}
          </div>
        )}

        {((mode === "single" && !overrideDate) || (mode === "range" && !range?.from)) && (
          <p className="mt-4 border-t border-border/60 pt-4 text-center text-xs text-muted-foreground">
            {mode === "single"
              ? "Odaberite datum u kalendaru da dodate izuzetak."
              : "Odaberite početni, zatim krajnji datum raspona."}
          </p>
        )}
      </section>

      {/* RIGHT: list */}
      <section className="rounded-xl border border-border/60 p-4">
        <h3 className="mb-3 text-sm font-semibold text-foreground">Upisani izuzeci</h3>

        {overrides.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border py-10 text-center text-sm text-muted-foreground">
            Još nema izuzetaka.
          </p>
        ) : (
          <div className="flex flex-col gap-5">
            {groups.map((group) => (
              <div key={group.key}>
                <p className="mb-2 text-xs font-medium text-muted-foreground">{group.label}</p>
                <ul className="divide-y divide-border/60 overflow-hidden rounded-lg border border-border/60">
                  {group.items.map((override) => {
                    const d = new Date(override.date + "T00:00:00");
                    const isBlock = override.type === "block";
                    return (
                      <li key={override.id} className="flex items-center gap-3 px-3 py-2.5">
                        <div className="flex w-9 shrink-0 flex-col items-center">
                          <span className="text-base font-semibold leading-none text-foreground tabular-nums">
                            {d.getDate()}
                          </span>
                          <span className="mt-0.5 text-[10px] text-muted-foreground">
                            {formatDateWithOptionsBs(d, { weekday: "short" }).replace(".", "")}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <span
                            className={cn(
                              "inline-flex items-center gap-1 text-xs font-medium",
                              isBlock ? "text-rose-600" : "text-emerald-600",
                            )}
                          >
                            {isBlock ? <Prohibit className="size-3" /> : <CalendarPlus className="size-3" />}
                            {isBlock ? "Blokirano" : "Dodato"}
                          </span>
                          <p className="text-sm text-muted-foreground">
                            {isBlock ? (
                              "Cijeli dan nedostupan"
                            ) : (
                              <span className="tabular-nums">
                                {override.startTime} - {override.endTime}
                              </span>
                            )}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => onDelete(override.id)}
                          disabled={isDeleting}
                          aria-label="Obriši izuzetak"
                          className="rounded-md p-1.5 text-muted-foreground/70 transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-40"
                        >
                          <Trash className="size-4" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
